import {
  Add01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Delete01Icon,
  Edit01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { ASSET_PERMISSIONS } from 'shared';
import { PermissionButton } from '@/components/shared/PermissionButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  useAssetStatuses,
  useCreateAssetStatus,
  useDeleteAssetStatus,
  useUpdateAssetStatus,
} from '@/hooks/use-asset-statuses';
import type { CreateAssetStatusInput, UpdateAssetStatusInput } from '@/lib/asset-status-client';

export const Route = createFileRoute('/_authenticated/asset-statuses')({
  component: AssetStatusesPage,
});

function AssetStatusForm({
  assetStatus,
  onSubmit,
}: {
  assetStatus?: any;
  onSubmit: (data: CreateAssetStatusInput | UpdateAssetStatusInput) => Promise<void>;
}) {
  const form = useForm({
    defaultValues: {
      name: assetStatus?.name || '',
      code: assetStatus?.code || '',
      color: assetStatus?.color || '#000000',
      isFailure: assetStatus?.isFailure || false,
      isRunning: assetStatus?.isRunning || false,
      description: assetStatus?.description || '',
      sortOrder: assetStatus?.sortOrder || 0,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="space-y-4"
    >
      <form.Field
        name="code"
        validators={{
          onChange: ({ value }) => (!value ? 'Code is required' : undefined),
        }}
      >
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Code</Label>
            <Input
              id={field.name}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="RUNNING"
            />
            {field.state.meta.errors && (
              <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
            )}
          </div>
        )}
      </form.Field>

      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => (!value ? 'Name is required' : undefined),
        }}
      >
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Name</Label>
            <Input
              id={field.name}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Running"
            />
            {field.state.meta.errors && (
              <p className="text-sm text-destructive">{field.state.meta.errors[0]}</p>
            )}
          </div>
        )}
      </form.Field>

      <form.Field name="color">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Color</Label>
            <div className="flex gap-2">
              <Input
                id={field.name}
                type="color"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className="w-16 h-10"
              />
              <Input
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="#000000"
              />
            </div>
          </div>
        )}
      </form.Field>

      <form.Field name="sortOrder">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Sort Order</Label>
            <Input
              id={field.name}
              type="number"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(Number(e.target.value))}
            />
          </div>
        )}
      </form.Field>

      <div className="flex gap-6">
        <form.Field name="isFailure">
          {(field) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id={field.name}
                checked={field.state.value}
                onBlur={field.handleBlur}
                onCheckedChange={(checked) => field.handleChange(checked)}
              />
              <Label htmlFor={field.name}>Is Failure</Label>
            </div>
          )}
        </form.Field>

        <form.Field name="isRunning">
          {(field) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id={field.name}
                checked={field.state.value}
                onBlur={field.handleBlur}
                onCheckedChange={(checked) => field.handleChange(checked)}
              />
              <Label htmlFor={field.name}>Is Running</Label>
            </div>
          )}
        </form.Field>
      </div>

      <form.Field name="description">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Description</Label>
            <Textarea
              id={field.name}
              value={field.state.value || ''}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          </div>
        )}
      </form.Field>

      <div className="flex gap-2 justify-end">
        <Button type="submit" disabled={form.state.isSubmitting}>
          {form.state.isSubmitting
            ? 'Saving...'
            : assetStatus
              ? 'Update Asset Status'
              : 'Create Asset Status'}
        </Button>
      </div>
    </form>
  );
}

function AssetStatusesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editingAssetStatus, setEditingAssetStatus] = useState<any>(null);
  const [deletingAssetStatus, setDeletingAssetStatus] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);

  const limit = 10;
  const { data, isLoading, error } = useAssetStatuses({ page, limit, search });
  const createAssetStatus = useCreateAssetStatus();
  const updateAssetStatus = useUpdateAssetStatus();
  const deleteAssetStatus = useDeleteAssetStatus();

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => {
        const status = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: status.color }} />
            <span>{status.name}</span>
          </div>
        );
      },
    },
    { accessorKey: 'code', header: 'Code' },
    {
      accessorKey: 'color',
      header: 'Color',
      cell: (info) => (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: info.getValue() as string }} />
          <span>{info.getValue() as string}</span>
        </div>
      ),
    },
    {
      accessorKey: 'isFailure',
      header: 'Is Failure',
      cell: (info) => ((info.getValue() as boolean) ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'isRunning',
      header: 'Is Running',
      cell: (info) => ((info.getValue() as boolean) ? 'Yes' : 'No'),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditingAssetStatus(row.original);
              setShowForm(true);
            }}
          >
            <HugeiconsIcon icon={Edit01Icon} className="size-4" />
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeletingAssetStatus(row.original)}
          >
            <HugeiconsIcon icon={Delete01Icon} className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: data?.totalPages || 0,
    state: {
      pagination: { pageIndex: page - 1, pageSize: limit },
    },
    manualPagination: true,
  });

  const closeForm = () => {
    setShowForm(false);
    setEditingAssetStatus(null);
  };

  const handleCreate = async (input: CreateAssetStatusInput) => {
    await createAssetStatus.mutateAsync(input);
    closeForm();
  };

  const handleUpdate = async (input: UpdateAssetStatusInput) => {
    if (!editingAssetStatus) return;
    await updateAssetStatus.mutateAsync({ id: editingAssetStatus.id, input });
    closeForm();
  };

  const handleDelete = async () => {
    if (!deletingAssetStatus) return;
    await deleteAssetStatus.mutateAsync(deletingAssetStatus.id);
    setDeletingAssetStatus(null);
  };

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-destructive">Error loading asset statuses</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Asset Statuses</h1>
          <p className="text-muted-foreground">Manage the different states an asset can be in</p>
        </div>
        <Button
          onClick={() => {
            setEditingAssetStatus(null);
            setShowForm(true);
          }}
        >
          <HugeiconsIcon icon={Add01Icon} className="size-4 mr-2" />
          Add Asset Status
        </Button>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (open) {
            setShowForm(true);
          } else {
            closeForm();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAssetStatus ? 'Edit Asset Status' : 'Create Asset Status'}
            </DialogTitle>
          </DialogHeader>
          <AssetStatusForm
            assetStatus={editingAssetStatus || undefined}
            onSubmit={editingAssetStatus ? handleUpdate : handleCreate}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deletingAssetStatus}
        onOpenChange={(open) => !open && setDeletingAssetStatus(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset Status</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingAssetStatus?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <PermissionButton asChild permission={ASSET_PERMISSIONS.DELETE}>
              <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
            </PermissionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader>
          <CardTitle>Asset Status List</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Search by name, code, or description..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="border-b px-4 py-2 text-left font-medium"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      No asset statuses found
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="px-4 py-2">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {data?.totalPages && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {page} of {data.totalPages} ({data.totalItems || data.data?.length || 0} total)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4 mr-1" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= data.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <HugeiconsIcon icon={ArrowRight01Icon} className="size-4 mr-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
